import {
  ButtonProps,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Button
} from '@mui/material'
import { Asset } from '../types'
import { useTranslation } from 'react-i18next'
import { useDialog, usePolicy } from '../hooks'
import { AssetStatus } from '../types/Asset'
import { Edit } from '@mui/icons-material'
import { MouseEvent } from 'react'
import AssetNameForm from './AssetNameForm'

export interface AssetNameEditButtonProps extends ButtonProps {
  asset: Asset
  component?: typeof IconButton | typeof Button
  afterUpdate?: (asset: Asset) => void
}

export default function AssetNameEditButton({
  asset,
  component = IconButton,
  afterUpdate,
  children,
  onClick,
  ...props
}: AssetNameEditButtonProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('asset')

  /**
   * The policy context.
   */
  const { canEditAsset } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * Handle the click event.
   */
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    onClick?.(event)
    openDialog()
  }

  function handleAfterSubmit(asset: Asset) {
    afterUpdate?.(asset)
    closeDialog()
  }

  return (
    <>
      {asset.status !== AssetStatus.INACTIVE && canEditAsset(asset) && (
        <>
          {component === IconButton ? (
            <Tooltip title={t('edit')}>
              <IconButton {...props} onClick={handleClick}>
                {children ?? <Edit />}
              </IconButton>
            </Tooltip>
          ) : (
            <Button {...props} onClick={handleClick}>
              {(children ?? asset.name) ? t('editName') : t('addName')}
            </Button>
          )}
        </>
      )}

      <Dialog open={open} maxWidth="sm" fullWidth onClose={closeDialog}>
        <DialogTitle>{asset.name ? t('editName') : t('addName')}</DialogTitle>
        <DialogContent>
          <AssetNameForm asset={asset} afterSubmit={handleAfterSubmit} onCancel={closeDialog} />
        </DialogContent>
      </Dialog>
    </>
  )
}
